import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

type CookieCategory = 'necessary' | 'analytics' | 'marketing';

type CookiePreferences = Record<CookieCategory, boolean>;

const STORAGE_KEY = 'invirtus.cookieConsent.v1';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

type StoredConsent = {
  preferences: CookiePreferences;
  decidedAt: string;
};

const readStoredConsent = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
};

const writeStoredConsent = (preferences: CookiePreferences) => {
  const payload: StoredConsent = {
    preferences,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const CookieConsent = () => {
  const { t } = useTranslation('marketing');
  const [open, setOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored?.preferences) {
      setPreferences({ ...defaultPreferences, ...stored.preferences });
    }
  }, []);

  const persist = (next: CookiePreferences) => {
    setPreferences(next);
    writeStoredConsent(next);
  };

  const closeAll = () => {
    setOpen(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    persist({ necessary: true, analytics: true, marketing: true });
    closeAll();
  };

  const handleRefuseAll = () => {
    persist({ necessary: true, analytics: false, marketing: false });
    closeAll();
  };

  const handleSavePreferences = () => {
    persist(preferences);
    closeAll();
  };

  const togglePreference = (key: Exclude<CookieCategory, 'necessary'>) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="tab"
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t('cookies.tab_aria')}
            title={t('cookies.tab_title')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            whileHover={{ opacity: 1, scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-5 left-5 z-50 flex h-9 w-9 items-center justify-center rounded-full border border-dark-invirtus/12 bg-white/90 text-dark-invirtus/70 shadow-[0_2px_10px_-2px_rgba(23,29,39,0.12)] backdrop-blur-sm transition-colors hover:text-dark-invirtus"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 2.5c.5 0 1 .04 1.5.12-.36.55-.5 1.22-.36 1.9.27 1.32 1.5 2.2 2.83 2.02.14 1.18 1.04 2.16 2.23 2.32.66.09 1.3-.1 1.78-.48.32.84.52 1.74.52 2.7 0 4.7-3.8 8.5-8.5 8.5S3.5 15.78 3.5 11.08c0-4.7 3.8-8.58 8.5-8.58z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <circle cx="8.5" cy="11" r="0.9" fill="currentColor" />
              <circle cx="13" cy="14.5" r="0.9" fill="currentColor" />
              <circle cx="15.5" cy="11.5" r="0.7" fill="currentColor" />
              <circle cx="10" cy="15" r="0.6" fill="currentColor" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeAll}
              className="fixed inset-0 z-40 bg-dark-invirtus/20 backdrop-blur-[2px]"
            />

            <motion.aside
              key="panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-consent-title"
              initial={{ x: -40, y: 20, opacity: 0 }}
              animate={{ x: 0, y: 0, opacity: 1 }}
              exit={{ x: -40, y: 20, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 240, damping: 28 }}
              className="fixed bottom-6 left-6 z-50 w-[calc(100vw-3rem)] max-w-[420px] overflow-hidden rounded-[20px] bg-light text-dark-invirtus shadow-[0_30px_80px_-20px_rgba(23,29,39,0.35)]"
            >
              {/* Hairline accent stripe */}
              <div className="absolute inset-y-0 left-0 w-[3px] bg-dark-invirtus" />

              <div className="relative p-7 sm:p-8">
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="mb-5 flex items-start justify-between gap-4"
                >
                  <div>
                    <div className="mb-2 flex items-center gap-2 font-heading text-[10px] uppercase tracking-[0.28em] text-grey-muted">
                      <span className="inline-block h-[1px] w-5 bg-dark-invirtus/40" />
                      {t('cookies.notice')}
                    </div>
                    <h2
                      id="cookie-consent-title"
                      className="font-heading text-[22px] font-semibold leading-tight tracking-tight"
                    >
                      {t('cookies.title')}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={closeAll}
                    aria-label={t('cookies.close_aria')}
                    className="-mr-2 -mt-2 rounded-full p-2 text-grey transition-colors hover:bg-dark-invirtus-soft hover:text-dark-invirtus"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </motion.div>

                {/* Body copy */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.14 }}
                  className="space-y-4 text-[13.5px] leading-relaxed text-grey"
                >
                  <p>
                    {t('cookies.body_intro')}{' '}
                    <Link
                      to="/privacy"
                      className="font-medium text-dark-invirtus underline decoration-dark-invirtus/30 underline-offset-4 transition-colors hover:decoration-dark-invirtus"
                    >
                      {t('cookies.privacy_link')}
                    </Link>
                    .
                  </p>

                  {/* Legal note — editorial blockquote */}
                  <div className="relative pl-4">
                    <span aria-hidden className="absolute left-0 top-1 bottom-1 w-[2px] bg-dark-invirtus" />
                    <div className="mb-1.5 flex items-center gap-2 font-heading text-[9px] uppercase tracking-[0.3em] text-grey-muted">
                      <span>{t('cookies.law_label')}</span>
                      <span className="inline-block h-[3px] w-[3px] rounded-full bg-grey-muted" />
                      <span>{t('cookies.cndp_label')}</span>
                    </div>
                    <p className="text-[12.5px] leading-relaxed text-grey">
                      {t('cookies.legal_body')}
                    </p>
                  </div>
                </motion.div>

                {/* Preferences */}
                <AnimatePresence initial={false}>
                  {showPreferences && (
                    <motion.div
                      key="prefs"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 space-y-1 border-t border-dark-invirtus/10 pt-5">
                        <PreferenceRow
                          title={t('cookies.essential_title')}
                          description={t('cookies.essential_desc')}
                          alwaysOnLabel={t('cookies.always_on')}
                          toggleAriaLabel={t('cookies.toggle_aria', { title: t('cookies.essential_title') })}
                          checked
                          locked
                        />
                        <PreferenceRow
                          title={t('cookies.analytics_title')}
                          description={t('cookies.analytics_desc')}
                          alwaysOnLabel={t('cookies.always_on')}
                          toggleAriaLabel={t('cookies.toggle_aria', { title: t('cookies.analytics_title') })}
                          checked={preferences.analytics}
                          onChange={() => togglePreference('analytics')}
                        />
                        <PreferenceRow
                          title={t('cookies.marketing_title')}
                          description={t('cookies.marketing_desc')}
                          alwaysOnLabel={t('cookies.always_on')}
                          toggleAriaLabel={t('cookies.toggle_aria', { title: t('cookies.marketing_title') })}
                          checked={preferences.marketing}
                          onChange={() => togglePreference('marketing')}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actions */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 }}
                  className="mt-6 space-y-2.5"
                >
                  {showPreferences ? (
                    <button
                      type="button"
                      onClick={handleSavePreferences}
                      className="group/btn relative w-full overflow-hidden rounded-full bg-dark-invirtus px-5 py-3 font-heading text-[12px] font-medium uppercase tracking-[0.2em] text-light transition-transform hover:-translate-y-[1px]"
                    >
                      <span className="relative">{t('cookies.save')}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAcceptAll}
                      className="group/btn relative w-full overflow-hidden rounded-full bg-dark-invirtus px-5 py-3 font-heading text-[12px] font-medium uppercase tracking-[0.2em] text-light transition-transform hover:-translate-y-[1px]"
                    >
                      <span className="relative">{t('cookies.accept_all')}</span>
                    </button>
                  )}

                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={handleRefuseAll}
                      className="rounded-full border border-dark-invirtus/20 bg-light px-4 py-2.5 font-heading text-[11px] font-medium uppercase tracking-[0.2em] text-dark-invirtus transition-colors hover:bg-dark-invirtus-soft"
                    >
                      {t('cookies.refuse')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPreferences((prev) => !prev)}
                      className="rounded-full border border-dashed border-dark-invirtus/20 bg-transparent px-4 py-2.5 font-heading text-[11px] font-medium uppercase tracking-[0.2em] text-dark-invirtus transition-colors hover:border-dark-invirtus/40"
                    >
                      {showPreferences ? t('cookies.hide_details') : t('cookies.customize')}
                    </button>
                  </div>
                </motion.div>

                {/* Footer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-5 flex items-center justify-between border-t border-dark-invirtus/10 pt-4"
                >
                  <Link
                    to="/privacy"
                    className="font-heading text-[10px] uppercase tracking-[0.24em] text-grey hover:text-dark-invirtus"
                  >
                    {t('cookies.policy_link')}
                  </Link>
                  <span className="font-heading text-[10px] uppercase tracking-[0.24em] text-grey-muted">
                    {t('cookies.brand_year')}
                  </span>
                </motion.div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

type PreferenceRowProps = {
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: () => void;
  alwaysOnLabel: string;
  toggleAriaLabel: string;
};

const PreferenceRow = ({ title, description, checked, locked, onChange, alwaysOnLabel, toggleAriaLabel }: PreferenceRowProps) => {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading text-[12.5px] font-medium tracking-tight text-dark-invirtus">
            {title}
          </span>
          {locked && (
            <span className="font-heading text-[8.5px] uppercase tracking-[0.24em] text-grey-muted">
              {alwaysOnLabel}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[12px] leading-snug text-grey">{description}</p>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={toggleAriaLabel}
        disabled={locked}
        onClick={onChange}
        className={`relative mt-0.5 h-[22px] w-[38px] flex-shrink-0 rounded-full transition-colors duration-200 ${
          checked ? 'bg-dark-invirtus' : 'bg-dark-invirtus/15'
        } ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className={`absolute top-[3px] h-4 w-4 rounded-full bg-light shadow-sm ${
            checked ? 'left-[19px]' : 'left-[3px]'
          }`}
        />
      </button>
    </div>
  );
};

export default CookieConsent;
